import _ from "lodash";
import { computed } from "mobx";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLeaseCompStore, useSaleCompStore } from "..";
import ILeaseComp, { LeaseCompPropertyType } from "../interfaces/ILeaseComp";
import IProperty from "../interfaces/IProperty";
import ISaleComp, { SaleCompPropertyType } from "../interfaces/ISaleComp";
import LeadsApiService from "../services/leadsApiService";
import { getPercentile, withinYears } from "../utils/utils";
import useCircleWithRadius, { ICircleInfo } from "./useCircleWithRadius";

export default function usePropertyEstimates(property: IProperty | undefined, radiusMiles: number = 15): IEstimates {
    const saleCompStore = useSaleCompStore();
    const leaseCompStore = useLeaseCompStore();
    const leadsApiService = useMemo(() => new LeadsApiService(), []);
    const circle = useCircleWithRadius(property?.centerPoint, radiusMiles);
    const [locationGrade, setLocationGrade] = useState<string | null>(null);
    const [isGradeLoading, setIsGradeLoading] = useState<boolean>(false);
    const saleComps = useMemo(() => circle ? computed(() => saleCompStore.saleComps.filter((sc) => circle.isPointInside(sc.centerPoint))).get() : [], [saleCompStore.saleComps, circle]);
    const leaseComps = useMemo(() => circle ? computed(() => leaseCompStore.leaseComps.filter((lc) => circle.isPointInside(lc.centerPoint))).get() : [], [leaseCompStore.leaseComps, circle]);

    const monthlyRentalResult = useMemo(() => {
        return property && circle ? computeMonthlyRental(property, leaseComps, circle) : createNoEstimate<ILeaseComp>();
    }, [property, leaseComps, circle]);

    const saleEstimatesResult = useMemo(() => {
        return property && circle ? computeSalePriceEstimates(property, saleComps, circle) : { salePricePerAcre: createNoEstimate<ISaleComp>(), salePricePerSqFt: createNoEstimate<ISaleComp>() };
    }, [property, saleComps, circle]);

    useEffect(() => {
        if (!property) {
            setLocationGrade(null);
            setIsGradeLoading(false);
            return;
        }

        let isRequestCancelled = false;
        setIsGradeLoading(true);

        leadsApiService.getLocationGrade(property.centerPoint)
            .then(grade => !isRequestCancelled && setLocationGrade(grade))
            .catch(() => {
                if (!isRequestCancelled) {
                    toast.error("Failed to compute location grade.");
                    setLocationGrade(null);
                }
            })
            .finally(() => !isRequestCancelled && setIsGradeLoading(false));

        return () => { isRequestCancelled = true; };
    }, [property, leadsApiService]);

    return useMemo(() => ({
        monthlyRental: monthlyRentalResult.value,
        isMonthlyRentalLimited: monthlyRentalResult.limited,
        monthlyRentalComparableComps: monthlyRentalResult.comparableComps,
        salePricePerAcre: saleEstimatesResult.salePricePerAcre.value,
        isSalePricePerAcreLimited: saleEstimatesResult.salePricePerAcre.limited,
        salePricePerAcreComparableComps: saleEstimatesResult.salePricePerAcre.comparableComps,
        salePricePerSqFt: saleEstimatesResult.salePricePerSqFt.value,
        isSalePricePerSqFtLimited: saleEstimatesResult.salePricePerSqFt.limited,
        salePricePerSqFtComparableComps: saleEstimatesResult.salePricePerSqFt.comparableComps,
        locationGrade,
        isGradeLoading,
    }), [monthlyRentalResult, saleEstimatesResult, locationGrade, isGradeLoading]);
}

function computeMonthlyRental(property: IProperty, leaseComps: ILeaseComp[], circle: ICircleInfo): IEstimateValue<ILeaseComp> {
    const hasBuilding = !!(property.buildingSize && property.buildingSize > 0);
    const baseSize = hasBuilding
        ? property.buildingSize!
        : property.landSize && property.landSize > 0
            ? property.landSize
            : undefined;

    if (!baseSize)
        return createNoEstimate();

    const items = _.chain(leaseComps)
        .filter(lc => {
            if (hasBuilding)
                return (
                    Boolean(
                        lc.propertyType === LeaseCompPropertyType.IOSWithBuilding &&
                        lc.buildingSize && lc.buildingSize > 1 && lc.buildingSize < 100000 &&
                        lc.tenantLandSize && lc.tenantLandSize > 1 && lc.tenantLandSize < 20 &&
                        withinYears(lc.signedDate, 3)
                    )
                );
            else
                return (
                    Boolean(
                        lc.propertyType === LeaseCompPropertyType.IOSNoBuilding &&
                        lc.tenantLandSize && lc.tenantLandSize > 1 && lc.tenantLandSize < 20 &&
                        withinYears(lc.signedDate, 3)
                    )
                );
        })
        .map((lc) => ({ comp: lc, distance: circle.getDistanceFromCenter(lc.centerPoint) }))
        .filter(r => r.distance <= 15)
        .sortBy(r => r.distance)
        .take(30)
        .map(({ comp, distance }) => {
            const compSize = hasBuilding ? comp.buildingSize : comp.tenantLandSize;
            if (!compSize) return null;
            const variance = Math.abs((baseSize - compSize) / baseSize);
            return {
                comp,
                distance,
                variance,
                derived: {
                    monthlyRentalRate: comp.totalRent / 12 / comp.tenantLandSize!,
                },
            } satisfies ICompComparable<ILeaseComp>;
        })
        .compact()
        .sortBy(r => r.variance)
        .take(15)
        .value();

    if (!items.length)
        return createNoEstimate();

    const winner = getPercentile(items, i => i.comp.landPsf, 0.75)!;
    const limited = items.length < 5;

    return createEstimate<ILeaseComp>(winner.comp.landPsf, limited, items, winner);
}

function computeSalePriceEstimates(property: IProperty, saleComps: ISaleComp[], circle: ICircleInfo)
    : { salePricePerAcre: IEstimateValue<ISaleComp>; salePricePerSqFt: IEstimateValue<ISaleComp> } {
    const hasBuilding = !!(property.buildingSize && property.buildingSize > 0);

    if (!hasBuilding && !(property.landSize && property.landSize > 0))
        return {
            salePricePerAcre: createNoEstimate(),
            salePricePerSqFt: createNoEstimate(),
        };

    const items = _.chain(saleComps)
        .filter((sc) => {
            if (hasBuilding) {
                return Boolean(
                    sc.propertyType === SaleCompPropertyType.IOSWithBuilding &&
                    sc.buildingSize && sc.buildingSize > 0 && sc.buildingSize < 100000 &&
                    sc.landSizeUsable > 1 && sc.landSizeUsable < 20 &&
                    withinYears(sc.saleDate, 3)
                );
            } else {
                return (
                    sc.propertyType === SaleCompPropertyType.IOSNoBuilding &&
                    sc.landSizeUsable > 1 && sc.landSizeUsable < 20 &&
                    withinYears(sc.saleDate, 3)
                );
            }
        })
        .map((sc) => ({ comp: sc, distance: circle.getDistanceFromCenter(sc.centerPoint) }))
        .filter((r) => r.distance <= 15)
        .sortBy((r) => r.distance)
        .take(30)
        .map(({ comp, distance }) => {
            let variance: number;
            if (hasBuilding) {
                if (!property.buildingSize || !comp.buildingSize) return null;
                variance = Math.abs((property.buildingSize - comp.buildingSize) / property.buildingSize);
            } else {
                if (!property.landSize || !comp.landSizeUsable) return null;
                variance = Math.abs((property.landSize - comp.landSizeUsable) / property.landSize);
            }
            return {
                comp,
                distance,
                variance,
            } satisfies ICompComparable<ISaleComp>;
        })
        .compact()
        .sortBy((r) => r.variance)
        .take(15)
        .value();

    if (!items.length)
        return {
            salePricePerAcre: createNoEstimate(),
            salePricePerSqFt: createNoEstimate(),
        };

    const limited = items.length < 5;
    const winnerPercentileAcre = getPercentile(items, i => i.comp.pricePerUsableAcre, 0.75)!;

    if (hasBuilding) {
        const winnerPercentilePSF = getPercentile(items, i => i.comp.buildingPerSqFt, 0.75)!;
        return {
            salePricePerAcre: createEstimate(winnerPercentileAcre?.comp.pricePerUsableAcre, limited, items, winnerPercentileAcre),
            salePricePerSqFt: createEstimate(winnerPercentilePSF.comp.buildingPerSqFt, limited, items, winnerPercentilePSF),
        };
    } else
        return {
            salePricePerAcre: createEstimate(winnerPercentileAcre.comp.pricePerUsableAcre, limited, items, winnerPercentileAcre),
            salePricePerSqFt: createNoEstimate(),
        };
}

function createEstimate<T>(value: number | null, limited: boolean, comps: ICompComparable<T>[], winner: ICompComparable<T> | null): IEstimateValue<T> {
    return { value, limited, comparableComps: { comps, winner } }
}

function createNoEstimate<T>(): IEstimateValue<T> {
    return createEstimate(null, true, [], null);
}

interface IEstimateValue<T> {
    value: number | null;
    limited: boolean;
    comparableComps: IEstimateCompsInfo<T>;
}

export interface IEstimateCompsInfo<T> {
    comps: ICompComparable<T>[];
    winner: ICompComparable<T> | null;
}

export interface ICompComparable<T> {
    comp: T
    distance: number;
    variance: number | null;
    derived?: Record<string, number | null>;
}

export interface IEstimates {
    monthlyRental: number | null;
    isMonthlyRentalLimited: boolean;
    monthlyRentalComparableComps: IEstimateCompsInfo<ILeaseComp>;
    salePricePerAcre: number | null;
    isSalePricePerAcreLimited: boolean;
    salePricePerSqFt: number | null;
    isSalePricePerSqFtLimited: boolean;
    salePricePerAcreComparableComps: IEstimateCompsInfo<ISaleComp>;
    salePricePerSqFtComparableComps: IEstimateCompsInfo<ISaleComp>;
    locationGrade: string | null;
    isGradeLoading: boolean;
}
